import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useMoney } from "@/components/crm/FinanceLock";
import { Panel, Empty, NativeSelect, slug } from "@/components/crm/common";

const TONE = { bad: "text-rose-500", good: "text-emerald-500" };

export function Kpi({ k }) {
  const { money } = useMoney();
  return (
    <Link to={k.link || "#"} data-testid={`role-kpi-${slug(k.label)}`} className="group rounded-2xl border border-zinc-100 bg-white p-4 transition-colors hover:border-[#C5A46D] fade-up">
      <div className="text-[12px] font-semibold text-zinc-500">{k.label}</div>
      <div className={`mt-1 text-2xl font-extrabold tracking-tight ${TONE[k.tone] || "text-zinc-900"}`}>{k.money ? money(k.value) : k.value}</div>
    </Link>
  );
}

export function Block({ b }) {
  return (
    <Panel testid={`role-block-${b.key}`} title={<span>{b.title} <span className="ml-1 font-mono text-xs text-zinc-400">{b.count}</span></span>}
      action={b.link && <Link to={b.link} className="inline-flex items-center gap-1 text-xs font-semibold text-[#C5A46D] hover:underline">Open<ArrowRight className="h-3.5 w-3.5" /></Link>}>
      {b.rows.length ? (
        <ul className="-my-2 divide-y divide-zinc-100">
          {b.rows.map((r, i) => (
            <li key={i}><Link to={r.link || b.link || "#"} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0"><div className="truncate text-sm font-bold text-zinc-900">{r.primary}</div><div className="truncate text-xs text-zinc-500">{r.secondary}</div></div>
              {r.badge && <span className="shrink-0 whitespace-nowrap rounded-md bg-[#C5A46D]/15 px-2 py-0.5 text-[11px] font-bold text-[#C5A46D]">{r.badge}</span>}
            </Link></li>
          ))}
        </ul>
      ) : <Empty text="Nothing here right now." />}
    </Panel>
  );
}

export function RoleDashboard({ data, onView }) {
  return (
    <div data-testid={`role-dashboard-${data.view}`} className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#C5A46D]">{data.label} dashboard</div>
          <div data-testid="role-dashboard-me" className="text-sm text-zinc-500">{data.me.code && <span className="font-mono font-bold text-[#C5A46D]">{data.me.code} · </span>}{[data.me.title, data.me.department].filter(Boolean).join(" · ")}</div>
        </div>
        {data.views.length > 1 && <NativeSelect testid="role-view-select" value={data.view} onChange={onView} options={data.views.map((v) => ({ value: v.key, label: v.label }))} />}
      </div>
      {!!data.my_kpis.length && <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{data.my_kpis.map((k) => <Kpi key={k.label} k={k} />)}</div>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">{data.kpis.map((k) => <Kpi key={k.label} k={k} />)}</div>
      <div className="grid gap-5 lg:grid-cols-2">{data.blocks.map((b) => <Block key={b.key} b={b} />)}</div>
    </div>
  );
}
